/**
 * Whether the login screen should tell the user their session ended.
 *
 * The URL cannot answer that on its own. `?session_ended=idle` is a link anyone
 * can type or bookmark, and a sign-out clears the token before it navigates, so a
 * real expiry and a hand-typed URL look the same to the login screen. The code
 * that actually ends a session leaves this one-use note in the tab; the notice is
 * shown only when the note is there, and reading it spends it — a refresh, Back,
 * Forward or a later visit find nothing and stay quiet.
 *
 * sessionStorage and not localStorage: the note belongs to the tab that was signed
 * out. Other tabs follow a sign-out on their own and have nothing to announce.
 */

import { isAnnounceableReason } from './authRoutes';

const NOTICE_KEY = 'session_end_notice';

/**
 * Long enough to cover the hard navigation a sign-out performs, short enough that
 * a note whose login screen never loaded cannot surface on some later visit.
 */
const NOTICE_TTL_MS = 2 * 60 * 1000;

export const markSessionEnded = (reason) => {
  if (!isAnnounceableReason(reason)) return;

  try {
    sessionStorage.setItem(NOTICE_KEY, JSON.stringify({ reason, at: Date.now() }));
  } catch {
    // Storage unavailable: the user signs in again, just without the explanation.
  }
};

/**
 * Reads the note and deletes it, so it is announced at most once.
 *
 * @returns {string|null} the reason to announce, or null when there is nothing
 * trustworthy to announce — no note, a stale one, or one that was tampered with.
 */
export const consumeSessionEndNotice = () => {
  let raw;
  try {
    raw = sessionStorage.getItem(NOTICE_KEY);
    sessionStorage.removeItem(NOTICE_KEY);
  } catch {
    return null;
  }

  if (!raw) return null;

  try {
    const { reason, at } = JSON.parse(raw);
    const age = Date.now() - at;
    if (!Number.isFinite(age) || age < 0 || age > NOTICE_TTL_MS) return null;
    return isAnnounceableReason(reason) ? reason : null;
  } catch {
    return null;
  }
};

export { NOTICE_KEY, NOTICE_TTL_MS };
